// /forgot-password — request a reset link by email. Always shows the SAME
// "check your email" confirmation regardless of whether the email is known
// (backend/src/services/passwordResetService.js's own contract) — this
// screen has no "email not found" state to build, by the endpoint's own
// design (root CLAUDE.md: never reveal whether an email exists). Reuses
// pages/LoginPage.module.css — identical card-form shape, no reason to ship
// a second near-duplicate stylesheet (portal/CLAUDE.md's 120KB budget).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';
import { useLanguage } from '../context/LanguageContext.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import PortalShell from '../components/PortalShell.jsx';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
// LoginPage.module.css — identical card-form shape (portal/CLAUDE.md budget).
import styles from './LoginPage.module.css';

async function requestReset(email) {
  return apiClient.post('/auth/password-reset/request', { email });
}

export default function ForgotPasswordPage() {
  const { t } = useLanguage();
  const [sent, setSent] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { email: '' } });

  const requestMutation = useMutation({ mutationFn: requestReset });
  const { toastOk, toastErr } = useActionFeedback();

  const onSubmit = ({ email }) => {
    requestMutation.mutate(email, {
      onSuccess: () => {
        setSent(true);
        toastOk(t('portal.forgotPassword.sentToast'));
      },
      onError: (err) => toastErr(err, ['PASSWORD_RESET_RATE_LIMITED']),
    });
  };

  if (sent) {
    return (
      <PortalShell backTo="/login" width="narrow">
        <div className={styles.card}>
          <h1 className={styles.title}>{t('portal.forgotPassword.sentTitle')}</h1>
          <p className={styles.subtitle}>{t('portal.forgotPassword.sentBody')}</p>
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell backTo="/login" width="narrow">
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <h1 className={styles.title}>{t('portal.forgotPassword.title')}</h1>
        <p className={styles.subtitle}>{t('portal.forgotPassword.subtitle')}</p>

        <label className={styles.field}>
          <span className={styles.label}>{t('portal.login.email')}</span>
          <input
            type="email"
            autoComplete="username"
            className={styles.input}
            {...register('email', { required: true })}
          />
          {errors.email && <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>}
        </label>

        <button type="submit" className={styles.submitButton} disabled={requestMutation.isPending}>
          {requestMutation.isPending && <Spinner />}{' '}
          {requestMutation.isPending
            ? t('portal.forgotPassword.submitting')
            : t('portal.forgotPassword.submit')}
        </button>

        <Link to="/login" className={styles.linkButton}>
          {t('portal.forgotPassword.backToLogin')}
        </Link>
      </form>
    </PortalShell>
  );
}
